// Faithful TS port of spike/iban-check.mjs (D-017/D-018/D-019). DO NOT "improve" the algorithm
// — the goal is byte-for-byte PARITY with the official XRechnung Schematron rules BR-DE-19
// (payee account) and BR-DE-20 (payer/debtor account under a PaymentMandate), not general IBAN
// correctness. The 28-fixture adversarial suite (testsuite/iban-fixtures/) and the 114-file
// parity gate both re-verify this port against the real KoSIT oracle in both directions.
//
// WHY THIS EXISTS: Saxon-JS computes `xs:integer(...) mod 97` in float64, so it loses precision
// on the ~24-digit number the IBAN check produces and BR-DE-19 (which uses xs:integer) fires
// spuriously on valid German IBANs. We re-evaluate BOTH IBAN rules in JS/BigInt and override
// Saxon-JS's result for them. Re-scan the stylesheets for the `mod 97` pattern on every
// artefact bump (UPDATING.md IBAN step).

import type { Syntax } from './types';

export interface OfficialIbanCheck {
  formatOk: boolean;
  checksumOk?: boolean;
}

/**
 * Mirrors the official algorithm EXACTLY, including its quirks:
 *  - whitespace is stripped, but case is NOT normalized before the format regex, so a lowercase
 *    country code FAILS format (verified behavior of the official rule, not a bug we introduce);
 *  - only the 2-letter country code is upper-cased before numeric conversion (the official XSLT
 *    scopes its upper-case() to substring(...,1,2)).
 */
export function officialIbanCheck(raw: string | null | undefined): OfficialIbanCheck {
  const stripped = (raw ?? '').replace(/[\s\n\r\t]/g, '');
  if (!/^[A-Z]{2}[0-9]{2}[a-zA-Z0-9]{0,30}$/.test(stripped)) return { formatOk: false };
  const country = stripped.slice(0, 2).toUpperCase();
  const checkDigits = stripped.slice(2, 4);
  const bban = stripped.slice(4);
  const rearranged = bban + country + checkDigits;
  const numeric = [...rearranged]
    .map((ch) => {
      const cp = ch.codePointAt(0)!;
      return String(cp > 64 ? cp - 55 : cp - 48);
    })
    .join('');
  return { formatOk: true, checksumOk: BigInt(numeric) % 97n === 1n };
}

/** True iff the official rule would consider this IBAN string valid (format + checksum). */
export function ibanIsValid(raw: string | null | undefined): boolean {
  const r = officialIbanCheck(raw);
  return r.formatOk === true && r.checksumOk === true;
}

interface IbanRulePaths {
  ubl: string;
  cii: string;
}

/**
 * Rules using the IBAN mod-97 pattern, mapped to the XML path of the account field they check.
 * Discovered by scanning the pinned stylesheets for `mod 97` (D-017) — re-scan on every bump.
 */
export const IBAN_RULES: Record<string, IbanRulePaths> = {
  'BR-DE-19': {
    ubl: '//cac:PaymentMeans[normalize-space(cbc:PaymentMeansCode)="58"]/cac:PayeeFinancialAccount/cbc:ID',
    cii: '//ram:SpecifiedTradeSettlementPaymentMeans[normalize-space(ram:TypeCode)="58"]/ram:PayeePartyCreditorFinancialAccount/ram:IBANID',
  },
  'BR-DE-20': {
    ubl: '//cac:PaymentMeans[normalize-space(cbc:PaymentMeansCode)="59"]/cac:PaymentMandate/cac:PayerFinancialAccount/cbc:ID',
    cii: '//ram:SpecifiedTradeSettlementPaymentMeans[normalize-space(ram:TypeCode)="59"]/ram:PayerPartyDebtorFinancialAccount/ram:IBANID',
  },
};

/**
 * Extract the IBAN field(s) an IBAN-checksum rule would examine, directly from raw XML text
 * (regex-based — good enough for our own field shapes). Returns the raw field values found.
 */
export function extractIbanFields(xml: string, ruleId: string, syntax: Syntax): string[] {
  if (ruleId === 'BR-DE-19') {
    return syntax === 'UBL'
      ? [...xml.matchAll(/<cbc:PaymentMeansCode>58<\/cbc:PaymentMeansCode>[\s\S]*?<cac:PayeeFinancialAccount>\s*(?:<!--.*?-->\s*)*<cbc:ID>([^<]*)<\/cbc:ID>/g)].map((m) => m[1])
      : [...xml.matchAll(/<ram:TypeCode>58<\/ram:TypeCode>[\s\S]*?<ram:PayeePartyCreditorFinancialAccount>\s*(?:<!--.*?-->\s*)*<ram:IBANID>([^<]*)<\/ram:IBANID>/g)].map((m) => m[1]);
  }
  if (ruleId === 'BR-DE-20') {
    return syntax === 'UBL'
      ? [...xml.matchAll(/<cbc:PaymentMeansCode>59<\/cbc:PaymentMeansCode>[\s\S]*?<cac:PaymentMandate>[\s\S]*?<cac:PayerFinancialAccount>\s*(?:<!--.*?-->\s*)*<cbc:ID>([^<]*)<\/cbc:ID>/g)].map((m) => m[1])
      : [...xml.matchAll(/<ram:TypeCode>59<\/ram:TypeCode>[\s\S]*?<ram:PayerPartyDebtorFinancialAccount>\s*(?:<!--.*?-->\s*)*<ram:IBANID>([^<]*)<\/ram:IBANID>/g)].map((m) => m[1]);
  }
  return [];
}

/**
 * Full re-evaluation (not mere suppression): decide whether BR-DE-19/BR-DE-20 SHOULD fire,
 * independent of what Saxon-JS computed. Returns { ruleId: fires } — true = rule violated.
 * If the relevant PaymentMeansCode context does not exist, the rule does not apply (fires=false),
 * matching the official rule's `not(code=X) or ...` gate.
 */
export function reevaluateIbanRules(xml: string, syntax: Syntax): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const ruleId of Object.keys(IBAN_RULES)) {
    const fields = extractIbanFields(xml, ruleId, syntax);
    const contextExists = new RegExp(
      syntax === 'UBL'
        ? `<cbc:PaymentMeansCode>${ruleId === 'BR-DE-19' ? '58' : '59'}</cbc:PaymentMeansCode>`
        : `<ram:TypeCode>${ruleId === 'BR-DE-19' ? '58' : '59'}</ram:TypeCode>`,
    ).test(xml);
    if (!contextExists) {
      out[ruleId] = false;
      continue;
    }
    const value = fields[0] ?? '';
    out[ruleId] = !ibanIsValid(value); // fires (rule violated) iff NOT a valid IBAN
  }
  return out;
}
